import React from 'react';
import './TermsOfServicePage.css';

function TermsOfServicePage() {
  return (
    <div className="terms-container">
      <div className="terms-content">
        <h1>Queer Data Network – Terms of Service</h1>
        <p className="last-updated">Last updated: January 7, 2026</p>

        <section>
          <h2>1. Overview</h2>
          <p>
            Queer Data Network ("QDN", "we", "us") provides an online community and resource hub 
            for LGBTQ+ people working with data, tech, research, and related fields. By creating 
            an account, accessing, or using QDN, you agree to these Terms of Service ("Terms"). 
            If you do not agree, do not use the site.
          </p>
        </section>

        <section>
          <h2>2. Eligibility and accounts</h2>
          <ul>
            <li>You must be at least 16 years old, or the minimum age of digital consent in your jurisdiction, to create an account.</li>
            <li>You are responsible for maintaining the confidentiality of your login credentials and for all activity under your account.</li>
            <li>You agree to provide accurate information to QDN and to update it as needed.</li>
          </ul>
        </section>

        <section>
          <h2>3. Community expectations</h2>
          <ul>
            <li>QDN is a queer-affirming, anti-harassment space. You agree to follow the Code of Conduct and all other posted policies.</li>
            <li>You will not use QDN to harass, threaten, or abuse others, or to promote hateful, discriminatory, or violent content.</li>
            <li>You will not dox, out, or otherwise disclose someone's identity or personal information without their explicit consent.</li>
            <li>You will not use QDN to promote surveillance, criminalization, or other harmful uses of data about queer and trans communities.</li>
          </ul>
        </section>

        <section>
          <h2>4. Data Collection for Platform Improvement</h2>
          <p>By using the QDN platform, you acknowledge and agree that:</p>
          <ul>
            <li><strong>Usage Analytics:</strong> We collect anonymous usage data (pageviews, engagement metrics) to improve the platform and serve our community better.</li>
            <li><strong>Automatic Collection:</strong> Pageview data is collected automatically when you browse QDN content. This collection is necessary for platform operation and improvement.</li>
            <li><strong>Retention Period:</strong> Analytics data is retained for up to 90 days, after which it is automatically deleted from our systems.</li>
            <li><strong>No Sale of Data:</strong> Your data will never be sold to third parties or used for advertising purposes.</li>
            <li><strong>Internal Use Only:</strong> All collected data is used exclusively by QDN administrators to improve site performance and user experience, understand community needs and interests, make informed decisions about content and features, and monitor platform health and engagement.</li>
          </ul>
          <p>
            For detailed information about what data we collect and how we use it, please review our <a href="/privacy-policy">Privacy Policy</a>.
          </p>
        </section>

        <section>
          <h2>5. User content and rights</h2>
          <ul>
            <li>You retain ownership of the content you post on QDN (including resources, discussions, and comments).</li>
            <li>By posting content, you grant QDN a non-exclusive, worldwide, royalty-free license to store, display, and distribute your content within the service, solely for operating and improving QDN.</li>
            <li>You confirm that you have the right to post any content you submit and that it does not infringe the rights or privacy of others.</li>
          </ul>
        </section>

        <section>
          <h2>6. Sensitive data and safety</h2>
          <ul>
            <li>You are responsible for ensuring that any data, examples, or case studies you share comply with applicable laws and ethical standards, including privacy and consent.</li>
            <li>You agree not to upload raw datasets or information that could reasonably identify specific individuals without their explicit, informed consent.</li>
            <li>QDN may remove content or restrict access where there is a reasonable concern about safety, privacy, or legal risk.</li>
          </ul>
        </section>

        <section>
          <h2>7. Moderation and enforcement</h2>
          <ul>
            <li>QDN may, at its discretion, edit, hide, or remove content that violates these Terms or the Code of Conduct.</li>
            <li>QDN may warn, suspend, or terminate accounts that engage in harassment, abuse, or other prohibited behavior, with or without prior notice.</li>
            <li>QDN is not obligated to monitor all content, but may review content and activity to maintain the safety and integrity of the community.</li>
          </ul>
        </section>

        <section>
          <h2>8. Intellectual property</h2>
          <ul>
            <li>The QDN name, logo, and site design are owned by QDN and may not be used without permission.</li>
            <li>You may not scrape, bulk-download, or systematically copy QDN content for use outside the service without explicit consent from QDN and, where applicable, from the original authors.</li>
          </ul>
        </section>

        <section>
          <h2>9. Service changes and availability</h2>
          <ul>
            <li>QDN is a small, community-run project and may change, suspend, or discontinue features or the entire service at any time.</li>
            <li>QDN does not guarantee uninterrupted or error-free operation and may experience downtime or data loss.</li>
          </ul>
        </section>

        <section>
          <h2>10. Disclaimers and limitation of liability</h2>
          <ul>
            <li>QDN is provided "as is" and "as available," without warranties of any kind, express or implied.</li>
            <li>QDN does not provide legal, medical, or professional advice; content on the site reflects the views of individual contributors.</li>
            <li>To the fullest extent permitted by law, QDN is not liable for indirect, incidental, or consequential damages arising from your use of the site or from user-generated content.</li>
          </ul>
        </section>

        <section>
          <h2>11. Third-party links</h2>
          <p>
            QDN may include links to third-party sites or services. QDN is not responsible for 
            the content, policies, or practices of those third parties.
          </p>
        </section>

        <section>
          <h2>12. Changes to these Terms</h2>
          <p>
            QDN may update these Terms from time to time. When changes are made, the "Last updated" 
            date will be revised. Continued use of QDN after changes take effect constitutes acceptance 
            of the updated Terms.
          </p>
        </section>

        <section>
          <h2>13. Contact</h2>
          <p>
            If you have questions or concerns about these Terms, or if you want to report a violation, 
            you can contact QDN at: <a href="mailto:contact@queerdatanetwork.com">contact@queerdatanetwork.com</a>
          </p>
        </section>
      </div>
    </div>
  );
}

export default TermsOfServicePage;
